import {
  ActionMenu,
  ChevronDownIcon,
  ChevronUpIcon,
  Flex,
  Text,
} from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import { FloorI } from '../types/types'

interface DarkFloorDropdownIProps {
  floors: FloorI[]
  selectedFloorName: string
  selectedFloorId: string
  onItemClick: (id: string) => void
  colorScheme?: ColorScheme
}

export const DarkFloorDropdown = ({
  floors,
  selectedFloorName,
  selectedFloorId,
  onItemClick,
  colorScheme = 'dark',
}: DarkFloorDropdownIProps) => {
  const prefix = colorScheme === 'light' ? 'light.' : ''

  const displayName =
    selectedFloorName ||
    (floors ?? []).find((floor) => floor.id === selectedFloorId)?.label ||
    ''

  const items = (floors ?? []).map((floor) => ({
    // Floor labels are display strings and not guaranteed unique — key on the id.
    key: floor.id,
    label: floor.label,
    onClick: () => onItemClick(floor.id),
    selected: floor.id === selectedFloorId,
  }))
  // Floors haven't loaded yet (or the facility genuinely has none) — opening
  // the menu would show an empty panel, which reads as the dropdown being
  // broken/hidden rather than as "nothing to select yet" (ENG-33730).
  const hasFloors = items.length > 0

  return (
    <ActionMenu
      colorScheme={colorScheme}
      fontSize='16px'
      items={items}
      maxHeight='40vh'
      trigger={({ onClick, isOpen }) => (
        <Flex
          _hover={
            hasFloors
              ? { bg: `${prefix}interactive.icon-button-hover-bg` }
              : undefined
          }
          alignItems='center'
          aria-disabled={!hasFloors}
          borderRadius='md'
          color={`${prefix}text.secondary`}
          cursor={hasFloors ? 'pointer' : 'not-allowed'}
          data-testid='floorPlanPage_header_floorDropdown'
          gap='8px'
          onClick={hasFloors ? onClick : undefined}
          opacity={hasFloors ? 1 : 0.5}
          px='12px'
          py='6px'
        >
          <Text
            fontFamily="'Soehne', sans-serif"
            fontSize='18px'
            fontWeight='400'
            lineHeight='28px'
            whiteSpace='nowrap'
          >
            {displayName}
          </Text>
          {isOpen ? (
            <ChevronUpIcon height={18} width={18} />
          ) : (
            <ChevronDownIcon height={18} width={18} />
          )}
        </Flex>
      )}
    />
  )
}
